import { isDrawerPanelView } from '@/shell/drawerPanel';
import { appPath } from '@featit/shared/addressing/paths';
import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { useStores } from '@/hooks/useStores';
import { Button } from '@/components/Button';
import { BOTTOM_BAR_ROW, ViewportBottomBar } from '@/components/AppViewport';
import { authApi, type IPendingAccountDeletion } from '@/services/AuthApi';
import { formatDeletionDate } from './accountDeletionDates';
import { useLocale, useT } from '@/i18n/LocaleProvider';
import { registerEnglishArea } from '@/i18n/catalogue';
import { banners } from '@featit/shared/i18n/messages/en/banners';

registerEnglishArea('banners', banners);

/**
 * "Your account will be deleted on …" (F-0224 §6).
 *
 * Asking for erasure signs the person out everywhere. Signing in again is how they undo it, and
 * signing in does not cancel anything by itself. So whoever signs in during the grace period has
 * to be told, on whatever screen they land on, or they will work for a week in an account that is
 * about to disappear.
 *
 * Rendered once, above the routes, beside `EmailVerificationBanner`. It reads `/auth/me`'s
 * `pendingDeletion` (I-1405) and, for a session that did not carry it, asks
 * `/auth/me/deletion/status` once per signed-in account, which is one indexed lookup. It asks
 * nothing on the public screens or for a verification session, which has no account to delete.
 */
export const PendingAccountDeletionBanner = observer(function PendingAccountDeletionBanner() {
  const t = useT();
  const locale = useLocale();
  const { authStore } = useStores();
  // I-1405: `/auth/me` carries this banner's whole answer, so the first commit draws it from the
  // session and the shell could hold the page for the chunk. Read through a ref: a later session
  // refresh must not undo a cancellation made here.
  const seed = authStore.user?.pendingDeletion;
  const seedRef = useRef(seed);
  seedRef.current = seed;
  const [pending, setPending] = useState<IPendingAccountDeletion | null>(() => seed ?? null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const userId = authStore.user?.id;
  const isVerificationSession = Boolean(authStore.isVerificationSession);

  useEffect(() => {
    if (!userId || isVerificationSession) {
      setPending(null);
      return;
    }
    if (seedRef.current !== undefined) {
      setPending(seedRef.current ?? null);
      return;
    }
    setPending(null);
    let cancelled = false;
    authApi
      .accountDeletionStatus()
      .then((status) => {
        if (!cancelled) setPending(status.pending);
      })
      .catch(() => {
        // Silent on purpose: this banner is a reminder, and Settings → Delete account shows the
        // same state with its own error. A failed reminder must not block the screen below it.
      });
    return () => {
      cancelled = true;
    };
  }, [userId, isVerificationSession]);

  if (!pending || isDrawerPanelView()) return null;

  const cancel = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await authApi.cancelAccountDeletion();
      setPending(null);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ViewportBottomBar order="account">
      <div
        role="status"
        data-bottom-bar="pending-account-deletion"
        className={`flex ${BOTTOM_BAR_ROW} flex-wrap items-center gap-2.5 border-t border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-950 dark:border-red-800/60 dark:bg-[#2b1a1a] dark:text-red-100`}
      >
        <CalendarClock className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="font-medium">
            {t('banners.deletion.willBeDeleted', {
              date: formatDeletionDate(pending.scheduledFor, locale),
            })}
          </span>{' '}
          {failed ? (
            <span className="text-red-700 dark:text-red-300">
              {t('banners.deletion.couldNotCancel')}
            </span>
          ) : (
            <Link
              to={appPath('/settings?section=delete-account')}
              className="text-red-800 evx-banner-action dark:text-red-200"
            >
              {t('banners.deletion.whatThisMeans')}
            </Link>
          )}
        </span>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={busy}
          onClick={() => void cancel()}
        >
          {t('banners.deletion.cancel')}
        </Button>
      </div>
    </ViewportBottomBar>
  );
});
